import { dateToLocalFormat } from 'helpers/dateToLocalFormat';
import { resolveBannerImage } from 'helpers/eventBanner';
import { TYPE_LABELS } from 'constants/eventTypes';
import EventTypeIcon from 'common/EventTypeIcon/EventTypeIcon';
import { Link } from 'react-router-dom';
import './EventCard.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Картка заходу — ADR-0004.
 *
 * Було: дата й місто окремим рядком над заголовком, тип заходу — текстовою
 * пігулкою серед інших прапорців. При довшій назві (три рядки й більше)
 * картка витягувалася вдвічі вище за банер, який мав бути головним — банер
 * займав лише верхню п'яту частину картки.
 *
 * Стало: дата й іконка типу лежать прямо на банері — це й повертає йому вагу
 * головного елемента, і звільняє блок під ним. Заголовок обрізається до двох
 * рядків (line-clamp): третій рядок і далі однаково не міняв суті картки,
 * лише розтягував її.
 *
 * Іконка типу сама по собі не самопояснювальна, тож на бейджі — своя
 * підказка (не нативний title: той із затримкою й без стилю), а над
 * календарем — міні-гайд з усіма типами одразу (Calendar.jsx). Іконка й
 * підпис узгоджені через constants/eventTypes і common/EventTypeIcon, щоб
 * картка й гайд не розійшлися між собою.
 */
export default function EventCard({ event, className = '' }) {
  const language = useLanguage();

  const typeLabel = TYPE_LABELS[event.type]?.[language] || event.type;
  const flags = [
    event.foreign && (language === 'en' ? 'International' : 'Міжнародний'),
    event.rating && (language === 'en' ? 'Rating' : 'Рейтинговий'),
    event.spartak && 'Спартак',
  ].filter(Boolean);

  const dates = event.end
    ? `${dateToLocalFormat(event.start).slice(0, 5)} - ${dateToLocalFormat(event.end).slice(0, 5)}`
    : dateToLocalFormat(event.start).slice(0, 5);

  const banner = resolveBannerImage(event.banner, 'horizontal');

  return (
    <Link className={`event-card ${className}`} to={`/calendar/${event.slug}`}>
      <span className='event-card-banner'>
        {banner ? (
          <img
            className='event-card-banner-img'
            src={banner.src}
            srcSet={banner.srcSet}
            sizes='(max-width: 700px) 100vw, 320px'
            alt=''
            loading='lazy'
          />
        ) : (
          <span className='event-card-banner-placeholder' aria-hidden='true' />
        )}

        <span className='event-card-date-chip tnum'>{dates}</span>

        {typeLabel && (
          <span className='event-card-type-badge' data-tooltip={typeLabel}>
            <EventTypeIcon type={event.type} size={20} />
            <span className='visually-hidden'>{typeLabel}</span>
          </span>
        )}
      </span>

      <div className='event-card-body'>
        {event.town && <span className='event-town'>{event.town}</span>}

        <h3 className='event-title'>{event.title}</h3>

        {flags.length > 0 && (
          <div className='event-tags'>
            {flags.map((flag) => (
              <span className='event-tag' key={flag}>
                {flag}
              </span>
            ))}
          </div>
        )}

        <p className='event-organizer'>
          {event.organizator}
          {/* Дані заходу прийшли з e-phan, а не від СГОСТУ. Показуємо це прямо:
              календар офіційний, і походження запису відвідувач має бачити. */}
          {event.source === 'ephan' && <span className='event-source'>e-phan</span>}
        </p>

        <span className='event-more'>
          {language === 'en' ? 'Details' : 'Детальніше'}
          <svg className='btn-arrow' width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
            <path
              d='M5 12h14M13 6l6 6-6 6'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </span>
      </div>
    </Link>
  );
}
